<script setup lang="ts">
import type { FrontendUserItem } from '@/api/modules/frontendUser'
import type { GlobalUserChatPayload, GlobalUserProfilePayload } from '@/composables/business/useGlobalUserProfile'
import type { UserProfileTab } from '@/views/user/user/components/profile/types'
import apiFrontendUser from '@/api/modules/frontendUser'
import OpenImChatRecordDialog from '@/components/AppDisplay/OpenImChatRecordDialog.vue'
import {
  OPEN_USER_CHAT_EVENT,
  OPEN_USER_PROFILE_EVENT,
  useGlobalUserProfile,
} from '@/composables/business/useGlobalUserProfile'
import eventBus from '@/utils/eventBus'
import UserProfileDialog from '@/views/user/user/components/profile/UserProfileDialog.vue'
import UserDetailForm from '@/views/user/user/components/UserDetailForm.vue'

defineOptions({ name: 'AppUserProfile' })

const { auth } = useAppAuth()
const { notifyUserProfileUpdated } = useGlobalUserProfile()
const profileVisible = shallowRef(false)
const profileUserId = shallowRef(0)
const profileTab = shallowRef<UserProfileTab>('overview')
const editVisible = shallowRef(false)
const editLoading = shallowRef(false)
const editUser = shallowRef<FrontendUserItem | null>(null)
const detailFormRef = ref<InstanceType<typeof UserDetailForm>>()
const chatVisible = shallowRef(false)
const chatPayload = shallowRef<GlobalUserChatPayload>({ userId: 0 })
const detailFormSubmitting = computed(() => Boolean(unref(detailFormRef.value?.submitting)))

function handleOpenProfile(payload: GlobalUserProfilePayload) {
  if (!auth('admin:user.user:detail')) {
    useFaToast().warning('当前账号没有查看用户详情的权限')
    return
  }
  profileUserId.value = Number(payload.userId || 0)
  profileTab.value = payload.tab || 'overview'
  profileVisible.value = profileUserId.value > 0
}

function handleOpenChat(payload: GlobalUserChatPayload) {
  if (!auth('admin:user.user:detail')) {
    useFaToast().warning('当前账号没有查看用户聊天记录的权限')
    return
  }
  chatPayload.value = { ...payload }
  chatVisible.value = payload.userId > 0
}

async function openEdit(user: FrontendUserItem) {
  if (!auth('admin:user.user:update')) {
    return
  }
  editLoading.value = true
  try {
    editUser.value = (await apiFrontendUser.detail(user.id)).data
    profileVisible.value = false
    editVisible.value = true
  }
  finally {
    editLoading.value = false
  }
}

async function submitEdit() {
  const submitted = await detailFormRef.value?.submit()
  if (!submitted || !editUser.value) {
    return false
  }
  notifyUserProfileUpdated(editUser.value.id)
  useFaToast().success('用户资料已更新')
  editVisible.value = false
  handleOpenProfile({ userId: editUser.value.id, tab: profileTab.value })
  return true
}

async function handleEditBeforeClose(action: string, done: () => void) {
  if (action !== 'confirm') {
    done()
    return
  }
  if (await submitEdit()) {
    done()
  }
}

function handleProfileUpdated() {
  notifyUserProfileUpdated(profileUserId.value)
}

onMounted(() => {
  eventBus.on(OPEN_USER_PROFILE_EVENT, handleOpenProfile)
  eventBus.on(OPEN_USER_CHAT_EVENT, handleOpenChat)
})

onUnmounted(() => {
  eventBus.off(OPEN_USER_PROFILE_EVENT, handleOpenProfile)
  eventBus.off(OPEN_USER_CHAT_EVENT, handleOpenChat)
})
</script>

<template>
  <UserProfileDialog
    v-model="profileVisible"
    v-model:tab="profileTab"
    :user-id="profileUserId"
    @edit="openEdit"
    @open-chat="userId => handleOpenChat({ userId })"
    @updated="handleProfileUpdated"
  />

  <FaModal
    v-model="editVisible"
    title="编辑用户资料"
    destroy-on-close
    class="sm:max-w-[1080px]"
    show-cancel-button
    :close-on-click-overlay="false"
    :confirm-button-loading="detailFormSubmitting"
    :before-close="handleEditBeforeClose"
  >
    <div v-loading="editLoading">
      <UserDetailForm ref="detailFormRef" mode="edit" :data="editUser" />
    </div>
  </FaModal>

  <OpenImChatRecordDialog
    v-model="chatVisible"
    mode="user"
    :user-id="chatPayload.userId"
    :conversation-id="chatPayload.conversationId"
    :target-user-id="chatPayload.targetUserId"
  />
</template>
